import type { LucideIcon } from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { cn } from "../../lib/cn";
import { crearRevelador, debeRevelarse, type Punto } from "../../lib/revelado";
import { BloqueDeUsuario } from "./BloqueDeUsuario";
import { Campanita } from "./Campanita";
import { EtiquetaEntorno } from "./entorno";
import { type GrupoNav, grupoContieneRuta, navegacionVisible } from "./navegacion";
import { PrintFlowMark } from "./PrintFlowMark";

/**
 * Sidebar de escritorio, **con el diseño de `imprenta`** (ADR 0027).
 *
 * Antes se había porteado solo la estructura. Esto suma lo que era trabajo de
 * diseño y se había perdido:
 *
 * - **El logo ES el botón de colapsar**, en una caja `rounded-xl` con `ring` y
 *   `shadow`. No hay un chevron aparte: la marca es el control.
 * - **Ítem activo en color de marca** (`bg-brand-50 text-brand-700`), no gris.
 * - **Flyout al hover cuando está colapsado**: un panel que sale a la derecha
 *   con el nombre, pegado al ítem (se le quita el redondeo derecho mientras
 *   está visible). No muestra información nueva, pero sin él el sidebar
 *   colapsado es peor de usar — es justo lo que el criterio viejo descartaba
 *   por "no funcional".
 * - Tipografía y espaciado de allá: `text-xl font-extrabold tracking-tight`,
 *   `p-6` expandido, `space-y-6` entre grupos, íconos de 20px.
 *
 * Se conserva lo ya porteado: colapso persistido y autocolapso por debajo de
 * 1024px sin pisar la preferencia.
 */

const CLAVE_COLAPSADO = "sidebar-colapsado";
const ANCHO_AUTOCOLAPSO = 1024;

/** Cuánto queda visible la pestaña tras el último movimiento del mouse. */
const DEMORA_PESTANA = 1000;

/**
 * Cuánto esperar antes de revisar si el puntero quedó sobre el sidebar.
 *
 * El ancho cambia con una transición de 300 ms: al colapsar, el sidebar puede
 * escaparse de abajo del cursor **sin disparar `mouseleave`**, y la pestaña
 * quedaría encendida sobre nada. Se revisa una vez terminada la transición.
 */
const RECHEQUEO_MS = 320;

/**
 * La pestaña de colapso — el mecanismo de `tagliapietra-portal`.
 *
 * Vive escondida **detrás** del sidebar (`-z-10` + `-translate-x-full`) y se
 * desliza hacia afuera cuando el mouse entra; se va sola al segundo de quietud.
 * Es un control que no ocupa lugar hasta que se lo busca: el logo sigue siendo
 * el botón principal de colapso, y esto es el atajo que se encuentra donde uno
 * lo va a buscar, en el borde.
 *
 * Con el teclado aparece por `focus-visible` —y no por `focus`—: después de un
 * click el botón queda enfocado, y con `focus` la pestaña se quedaba pintada
 * para siempre.
 */
function PestanaDeColapso({
  colapsado,
  alAlternar,
  sidebarRef,
}: {
  colapsado: boolean;
  alAlternar: () => void;
  sidebarRef: React.RefObject<HTMLElement | null>;
}) {
  const [visible, setVisible] = useState(false);
  const pestanaRef = useRef<HTMLButtonElement>(null);
  // `null` hasta el primer movimiento: `{0,0}` cae dentro del sidebar.
  const punteroRef = useRef<Punto | null>(null);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    const pestana = pestanaRef.current;
    if (!sidebar || !pestana) return;

    const revelador = crearRevelador(setVisible, DEMORA_PESTANA);
    const seguirPuntero = (e: MouseEvent) => {
      punteroRef.current = { x: e.clientX, y: e.clientY };
    };

    /**
     * El movimiento sobre el sidebar revela la pestaña y programa su apagado.
     *
     * **Salvo que venga de la pestaña misma.** La pestaña está DENTRO del
     * sidebar, así que sus `mousemove` burbujean hasta acá: sin este guard, el
     * mouse quieto sobre la pestaña la apagaba al segundo — el `mouseenter` de
     * la pestaña cancelaba el apagado y el `mousemove` del mismo movimiento,
     * que llega después, lo volvía a programar.
     */
    const alMoverseEnSidebar = (e: MouseEvent) => {
      if (e.target instanceof Node && pestana.contains(e.target)) return;
      revelador.mostrarYOcultar();
    };

    sidebar.addEventListener("mouseenter", alMoverseEnSidebar);
    sidebar.addEventListener("mousemove", alMoverseEnSidebar);
    sidebar.addEventListener("mouseleave", revelador.ocultar);
    // Sobre la pestaña no se auto-oculta: el mouse ya está donde tiene que ir.
    pestana.addEventListener("mouseenter", revelador.mostrar);
    pestana.addEventListener("mouseleave", revelador.mostrarYOcultar);
    document.addEventListener("mousemove", seguirPuntero, { passive: true });

    return () => {
      revelador.cancelar();
      sidebar.removeEventListener("mouseenter", alMoverseEnSidebar);
      sidebar.removeEventListener("mousemove", alMoverseEnSidebar);
      sidebar.removeEventListener("mouseleave", revelador.ocultar);
      pestana.removeEventListener("mouseenter", revelador.mostrar);
      pestana.removeEventListener("mouseleave", revelador.mostrarYOcultar);
      document.removeEventListener("mousemove", seguirPuntero);
    };
  }, [sidebarRef]);

  // Tras colapsar o expandir, el ancho cambió: se revisa dónde quedó el puntero.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `colapsado` dispara el rechequeo a propósito — no se lee adentro, pero es el cambio que hay que revalidar
  useEffect(() => {
    const timer = setTimeout(() => {
      const sidebar = sidebarRef.current;
      const pestana = pestanaRef.current;
      if (!sidebar || !pestana) return;
      setVisible(
        debeRevelarse(
          [sidebar.getBoundingClientRect(), pestana.getBoundingClientRect()],
          punteroRef.current,
        ),
      );
    }, RECHEQUEO_MS);
    return () => clearTimeout(timer);
  }, [colapsado, sidebarRef]);

  return (
    <button
      type="button"
      ref={pestanaRef}
      onClick={alAlternar}
      aria-expanded={!colapsado}
      aria-label={colapsado ? "Expandir barra lateral" : "Colapsar barra lateral"}
      className={cn(
        "-right-4 -translate-y-1/2 absolute top-1/2 grid h-13 w-[17px] place-items-center rounded-r-lg bg-sb-2 text-sb-muted shadow-sm transition-[opacity,transform,color] duration-200 hover:text-sb-text",
        "focus-visible:pointer-events-auto focus-visible:z-20 focus-visible:translate-x-0 focus-visible:opacity-100",
        visible
          ? "pointer-events-auto z-20 translate-x-0 opacity-100"
          : "-z-10 pointer-events-none -translate-x-full opacity-0",
      )}
    >
      <ChevronLeft
        className={cn(
          "h-3.5 w-3.5 transition-transform duration-200",
          colapsado ? "rotate-180" : "",
        )}
      />
    </button>
  );
}

export function Sidebar({
  puede,
  nombre,
  email,
  roles,
  alSalir,
}: {
  puede: (codigo: string) => boolean;
  nombre: string;
  email: string;
  roles: readonly string[];
  alSalir: () => void;
}) {
  const [preferencia, setPreferencia] = useState(
    () => localStorage.getItem(CLAVE_COLAPSADO) === "true",
  );
  const [pantallaAngosta, setPantallaAngosta] = useState(
    () => typeof window !== "undefined" && window.innerWidth < ANCHO_AUTOCOLAPSO,
  );

  const colapsado = pantallaAngosta || preferencia;

  useEffect(() => {
    localStorage.setItem(CLAVE_COLAPSADO, String(preferencia));
  }, [preferencia]);

  useEffect(() => {
    const alRedimensionar = () => setPantallaAngosta(window.innerWidth < ANCHO_AUTOCOLAPSO);
    window.addEventListener("resize", alRedimensionar);
    return () => window.removeEventListener("resize", alRedimensionar);
  }, []);

  const grupos = navegacionVisible(puede);
  const asideRef = useRef<HTMLElement>(null);

  return (
    <aside
      ref={asideRef}
      className={cn(
        // `relative` para la pestaña, que se posiciona contra este borde.
        // **Sin `border-r`**: la sidebar no está pegada a nada (ADR 0060). El
        // borde derecho era la línea que la ataba al contenido; ahora hay aire,
        // un anillo tenue y sombra — una superficie apoyada sobre el fondo.
        // Degradado de `tagliapietra-portal` (ADR 0061): `#2b2c30` arriba,
        // `#303136` abajo. Es la ÚNICA zona oscura del dashboard.
        "relative flex h-full flex-col rounded-xl bg-gradient-to-b from-sb to-sb-2 shadow-lg shadow-neutral-900/20 transition-all duration-300",
        colapsado ? "w-16" : "w-[260px]",
      )}
    >
      {/* La pantalla angosta colapsa sola: ahí la pestaña no se ofrece, porque
          expandir volvería a colapsarse al primer resize. */}
      {pantallaAngosta ? null : (
        <PestanaDeColapso
          colapsado={colapsado}
          alAlternar={() => setPreferencia((v) => !v)}
          sidebarRef={asideRef}
        />
      )}
      <div
        className={cn(
          "border-sb-border border-b transition-all duration-300",
          colapsado ? "p-3" : "p-6",
        )}
      >
        <div className={cn("flex items-center", colapsado ? "justify-center" : "gap-3")}>
          <button
            type="button"
            onClick={() => setPreferencia((v) => !v)}
            aria-label={colapsado ? "Expandir menú" : "Colapsar menú"}
            className={cn(
              "flex cursor-pointer items-center justify-center rounded-xl bg-white/10 shadow-sm ring-1 ring-white/10 transition-all duration-300 hover:bg-white/15",
              colapsado ? "h-10 w-10" : "h-12 w-12",
            )}
          >
            <PrintFlowMark
              className={cn("transition-all duration-300", colapsado ? "h-7 w-7" : "h-9 w-9")}
            />
          </button>

          {/* Se oculta con ancho y opacidad, no desmontando: así la transición
              del colapso es continua y el texto no salta. */}
          <div
            className={cn(
              "flex min-w-0 flex-1 flex-col transition-all duration-300",
              colapsado ? "w-0 overflow-hidden opacity-0" : "w-auto opacity-100",
            )}
          >
            <span className="font-extrabold text-sb-text text-xl tracking-tight">PrintFlow</span>
            <EtiquetaEntorno variante="sidebar" />
          </div>
        </div>
      </div>

      {/* El nav va envuelto para poder apoyarle el degradado encima: la
          navegación no entra entera —16 ítems en 654px— y sin nada que lo
          insinúe, el grupo «Sistema» simplemente no existe para quien no
          descubre que ahí se scrollea. Es el `::after` de
          `tagliapietra-portal`, que existe por lo mismo. */}
      <div className="relative min-h-0 flex-1">
        <nav
          className={cn(
            // `min-h-0` es lo que deja que el nav se ENCOJA: sin él, un hijo
            // flex con contenido largo crece hasta desbordar al hermano de
            // abajo — el pie terminaba 38px por debajo del viewport y su menú
            // no se podía ni abrir, porque el click caía fuera de la pantalla.
            "h-full space-y-6 overflow-y-auto py-6 transition-all duration-300",
            colapsado ? "px-2" : "px-3",
          )}
        >
          {grupos.map((grupo) => (
            <GrupoSidebar key={grupo.titulo ?? "principal"} grupo={grupo} colapsado={colapsado} />
          ))}
        </nav>

        {/* El degradado NO recibe clicks: es una pista visual, no una barrera. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-sb-2 to-transparent"
        />
      </div>

      <div className={cn("border-sb-border border-t", colapsado ? "p-2" : "p-4")}>
        {/* La campanita va en el pie y no arriba: `imprenta` no tiene ninguna en
            escritorio —la suya es un placeholder de mobile— asi que no hay
            diseño que portear, y aca conviene junto a lo que es "de la persona"
            y no de la navegacion.

            Es una FILA del menú, con su nombre y su cuenta (ADR 0059): un ícono
            suelto no dice qué es ni cuánto hay, y era lo único del sidebar sin
            etiqueta cuando está expandido. */}
        <div className="mb-1">
          <Campanita lado="right" colapsado={colapsado} />
        </div>
        <BloqueDeUsuario
          nombre={nombre}
          email={email}
          roles={roles}
          colapsado={colapsado}
          alSalir={alSalir}
        />
      </div>
    </aside>
  );
}

/**
 * Un grupo de la navegación, **colapsable** — como en `tagliapietra-portal`
 * (ADR 0062).
 *
 * Arranca abierto **solo si contiene la pantalla actual**. Con los 16 ítems
 * abiertos la navegación no entraba en la pantalla; así se ve el grupo donde uno
 * está y los demás quedan a un click.
 *
 * El bloque principal —el que no tiene título— **no se colapsa**: es el trabajo
 * de todos los días, y esconderlo detrás de un encabezado sería cobrarle un
 * click a lo que más se usa.
 *
 * Con el sidebar colapsado tampoco: ahí no hay títulos, solo íconos, y no queda
 * de dónde agarrar el acordeón.
 */
function GrupoSidebar({ grupo, colapsado }: { grupo: GrupoNav; colapsado: boolean }) {
  const { pathname } = useLocation();
  const contieneActiva = grupoContieneRuta(grupo.items, pathname);
  const [abierto, setAbierto] = useState(contieneActiva);

  // Si la navegación lleva a una pantalla de ESTE grupo, se abre solo: quedarse
  // cerrado escondería la ubicación actual.
  useEffect(() => {
    if (contieneActiva) setAbierto(true);
  }, [contieneActiva]);

  const items = grupo.items.map((item) => (
    <ItemSidebar
      key={item.ruta}
      ruta={item.ruta}
      texto={item.texto}
      icono={item.icono}
      colapsado={colapsado}
    />
  ));

  if (!grupo.titulo || colapsado) return <div>{items}</div>;

  return (
    <div>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className="mb-1 flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left font-semibold text-sb-muted text-xs uppercase tracking-wider transition-colors hover:bg-white/5 hover:text-sb-text-2"
      >
        <span className="flex-1">{grupo.titulo}</span>
        <ChevronRight
          aria-hidden="true"
          className={cn("h-3.5 w-3.5 transition-transform duration-200", abierto && "rotate-90")}
        />
      </button>

      {/*
       * `grid-rows-[0fr] → [1fr]` para poder animar una altura que no se sabe.
       *
       * `invisible` además del alto: con solo el `0fr` los ítems siguen en el
       * DOM y **el tabulador los alcanza** aunque no se vean. Es la misma
       * solución del portal, y es animable, así que la transición se conserva.
       */}
      <div
        className={cn(
          "grid overflow-hidden transition-[grid-template-rows,visibility] duration-200",
          abierto ? "visible grid-rows-[1fr]" : "invisible grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0">{items}</div>
      </div>
    </div>
  );
}

/**
 * Un ítem, con su flyout.
 *
 * El flyout **solo existe colapsado**: es lo que devuelve el nombre que el
 * colapso se llevó. Se pega al ítem quitándole el redondeo del lado derecho
 * mientras está visible, así se leen como una sola pieza.
 */
function ItemSidebar({
  ruta,
  texto,
  icono: Icono,
  colapsado,
}: {
  ruta: string;
  texto: string;
  icono: LucideIcon;
  colapsado: boolean;
}) {
  const [encima, setEncima] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const ubicacion = useLocation();
  const activo = ubicacion.pathname === ruta || ubicacion.pathname.startsWith(`${ruta}/`);

  /**
   * Dónde pintar el flyout, en coordenadas de viewport.
   *
   * Va `fixed` y no `absolute` porque el nav **scrollea**: con `absolute`, el
   * flyout es hijo de un contenedor con `overflow`, y el navegador lo recorta en
   * el borde del sidebar. La alternativa era dejar el nav en `overflow: visible`
   * —lo que había— y eso impedía que el nav se encogiera: el pie terminaba
   * fuera de la pantalla.
   */
  const caja = encima && colapsado ? linkRef.current?.getBoundingClientRect() : null;

  return (
    <div className="relative">
      <NavLink
        ref={linkRef}
        to={ruta}
        // Colapsado el texto no se renderiza, así que el link queda SIN nombre
        // accesible: un lector de pantalla anuncia "link" y nada más. `imprenta`
        // tiene el mismo hueco. El `aria-label` lo cubre sin cambiar lo visual.
        aria-label={colapsado ? texto : undefined}
        // Los handlers van en el LINK y no en el contenedor: es el elemento
        // interactivo. Y se escucha `focus` además de `hover` — `imprenta` solo
        // reacciona al mouse, así que con el sidebar colapsado quien navega con
        // teclado no ve nunca el nombre del ítem.
        onMouseEnter={() => setEncima(true)}
        onMouseLeave={() => setEncima(false)}
        onFocus={() => setEncima(true)}
        onBlur={() => setEncima(false)}
        className={cn(
          "mb-0.5 flex items-center rounded-md px-3 py-2 font-medium text-sm transition-colors",
          !colapsado && "gap-3",
          colapsado && "justify-center px-2",
          // Activo: **píldora translúcida y blanco**, sin color de acento — es
          // como lo resuelve el portal. Un `bg-brand-50` sobre oscuro sería un
          // parche claro en el único lugar oscuro de la interfaz.
          activo ? "bg-white/10 text-white" : "text-sb-text hover:bg-white/5",
          colapsado && encima && "rounded-r-none",
        )}
      >
        <Icono className="h-5 w-5 shrink-0" />
        {colapsado ? null : texto}
      </NavLink>

      {caja ? (
        <div
          style={{ top: caja.top, left: caja.right, height: caja.height }}
          className={cn(
            "fixed z-50 flex items-center rounded-r-md shadow-lg",
            activo ? "bg-white/15" : "bg-sb-raised",
          )}
        >
          <span
            className={cn(
              "whitespace-nowrap px-3 pr-4 font-medium text-sm",
              activo ? "text-white" : "text-sb-text",
            )}
          >
            {texto}
          </span>
        </div>
      ) : null}
    </div>
  );
}
